import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, RotateCcw } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageTitle, Btn, Toggle } from "./ui";
import { FieldTree } from "./FieldTree";

const SECTIONS = [
  { key: "seo", label: "Homepage SEO title & description" },
  { key: "nav", label: "Navigation menu" },
  { key: "ticker", label: "Top ticker messages" },
  { key: "header", label: "Header buttons" },
  { key: "hero", label: "Hero section (background image)", toggle: true },
  { key: "promo", label: "Welcome bonus banner", toggle: true },
  { key: "statsbar", label: "Stats bar (count-up numbers)", toggle: true },
  { key: "exch", label: "Our exchanges (cards managed under Exchanges)", toggle: true },
  { key: "exchange", label: "Live exchange", toggle: true },
  { key: "bets", label: "Cricket bet types", toggle: true },
  { key: "games", label: "Sports & casino games", toggle: true },
  { key: "fixtures", label: "Upcoming fixtures", toggle: true },
  { key: "how", label: "How it works & stats", toggle: true },
  { key: "proofs", label: "Withdrawal proof (images managed under Withdraw Proofs)", toggle: true },
  { key: "cities", label: "All-India cities & map", toggle: true },
  { key: "rating", label: "Review rating summary", toggle: true },
  { key: "testi", label: "Testimonials carousel", toggle: true },
  { key: "pay", label: "Payments & features", toggle: true },
  { key: "guides", label: "Related guides links", toggle: true },
  { key: "faq", label: "FAQ", toggle: true },
  { key: "winner", label: "Winner popup", toggle: true },
  { key: "modal", label: "Get ID popup" },
  { key: "demo", label: "Floating button label" },
  { key: "footer", label: "Footer, disclaimer, territories & 18+ notice" },
];

export default function ContentEditor() {
  const [c, setC] = useState(null);
  const [lang, setLang] = useState("en");
  const [open, setOpen] = useState("hero");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/content").then(({ data }) => setC(data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", c);
      setC(data);
      toast.success("Homepage saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const reset = async (key) => {
    if (!window.confirm("Reset this section to the original text (both languages)?")) return;
    const { data } = await api.post(`/admin/content/reset/${key}`);
    setC(data);
    toast.success("Section reset");
  };

  if (!c) return <p className="text-zinc-500">Loading…</p>;
  return (
    <div data-testid="admin-content-editor">
      <PageTitle title="Homepage Sections" sub="Edit text, lists and visibility for every homepage section. Images & icons are shared by both languages — set them in English.">
        <div className="flex rounded-full border border-[#D4AF37]/30 p-0.5">
          {["en", "hi"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`content-lang-${l}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold ${lang === l ? "bg-[#D4AF37] text-black" : "text-[#D4AF37]"}`}>{l === "en" ? "English" : "हिंदी"}</button>
          ))}
        </div>
        <Btn onClick={save} disabled={saving} data-testid="content-save">{saving ? "Saving…" : "Save changes"}</Btn>
      </PageTitle>
      <div className="space-y-3">
        {SECTIONS.map((s) => (
          <div key={s.key} className="rounded-2xl border border-[#D4AF37]/20 bg-[#101117]" data-testid={`section-${s.key}`}>
            <div className="flex items-center justify-between gap-3 p-4">
              <button onClick={() => setOpen(open === s.key ? "" : s.key)} data-testid={`section-toggle-${s.key}`} className="flex flex-1 items-center gap-3 text-left">
                <ChevronDown size={16} className={`text-[#D4AF37] transition-transform ${open === s.key ? "rotate-180" : ""}`} />
                <span className="text-sm font-medium text-white">{s.label}</span>
              </button>
              {s.toggle && (
                <Toggle checked={c.sections[s.key] !== false} testid={`section-visible-${s.key}`} label={<span className="hidden text-xs sm:inline">{c.sections[s.key] !== false ? "Visible" : "Hidden"}</span>}
                  onChange={(v) => setC({ ...c, sections: { ...c.sections, [s.key]: v } })} />
              )}
            </div>
            {open === s.key && (
              <div className="border-t border-white/5 p-4 sm:p-6">
                <FieldTree value={c[lang][s.key]} path={s.key} onChange={(v) => setC({ ...c, [lang]: { ...c[lang], [s.key]: v } })} />
                <button onClick={() => reset(s.key)} data-testid={`section-reset-${s.key}`} className="mt-5 flex items-center gap-2 text-xs text-zinc-500 hover:text-white"><RotateCcw size={13} /> Reset to default</button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="sticky bottom-4 mt-6 flex justify-end"><Btn onClick={save} disabled={saving} data-testid="content-save-bottom">{saving ? "Saving…" : "Save changes"}</Btn></div>
    </div>
  );
}
